import { Store } from "../../../_node_modules/@jwplayer/fe-common/module/store/index.js";
import { uploadManager } from "./manager.js";
import { poll } from "../../../_node_modules/@jwplayer/fe-common/module/poller/index.js";
import { castDraft } from "immer";

//#region ../base/upload/base.ts
const DEFAULT_POLL_INTERVAL = 3e3;
const initialUploadState = {
	state: "idle",
	progress: 0,
	errors: [],
	asset: void 0,
	info: void 0,
	pollAssetState: "n/a"
};
const incId = (() => {
	let id = 0;
	return {
		get() {
			id = id + 1;
			return id;
		},
		reset() {
			id = 0;
		}
	};
})();
var Upload = class {
	type;
	/**
	* Whether this upload type can be paused
	*/
	canPause = false;
	metadata;
	createUpload;
	readAsset;
	poller;
	/**
	* Upload identifier
	*/
	_identifier;
	get identifier() {
		return this._identifier;
	}
	index = incId.get();
	store = new Store({ ...initialUploadState });
	setState = this.store.setState.bind(this.store);
	getState = this.store.getState.bind(this.store);
	subscribe = this.store.subscribe.bind(this.store);
	when = (() => {
		const fn = (...args) => {
			return this.store.when(...args);
		};
		fn.state = (requiredState) => {
			return this.store.when((s) => s.state, (s) => s === requiredState);
		};
		fn.assetStatus = (requiredStatus) => {
			return this.store.when((s) => s.asset?.status, (s) => s === requiredStatus);
		};
		return fn;
	})();
	debug = false;
	_log(...args) {
		if (!this.debug) return;
		console.debug("[ UPLOAD ]", ...args, JSON.parse(JSON.stringify(this)));
	}
	constructor(params) {
		this.type = params.type;
		this.metadata = params.metadata;
		this._identifier = params.getIdentifier(null);
		this.createUpload = async () => {
			try {
				const { uploadId, uploadLink, uploadToken, asset } = await params.createUpload();
				this._log("Created upload info", {
					uploadId,
					uploadLink,
					uploadToken
				});
				this.store.setState((s) => {
					s.info = {
						uploadId,
						uploadLink,
						uploadToken
					};
				});
				if (asset) {
					this._log("Created asset", asset);
					this.store.setState((s) => {
						s.asset = castDraft(asset);
					});
					this._identifier = params.getIdentifier(asset.id);
					if (params.pollAssetCfg) {
						const { stopOnStatus, interval: pollInterval, maxTries } = params.pollAssetCfg;
						this.poller = poll({
							initialData: asset,
							requestFn: async ({ signal }) => {
								return this.readAsset({ signal });
							},
							assertFn: (asset$1) => {
								if (!asset$1) return false;
								this._log(`Asserting asset state. Required: ${stopOnStatus.join(", ")}. Current: ${asset$1.status}`);
								return Boolean(stopOnStatus.includes(asset$1.status));
							}
						}, {
							timeBetweenRetries: pollInterval ?? DEFAULT_POLL_INTERVAL,
							maxTries
						});
					}
				}
			} catch (err) {
				this._log("Error creating asset", err);
				this.store.setState((s) => {
					s.state = "failed";
					s.errors = [...s.errors, err];
				});
				this.handleUploadFailed();
				throw err;
			}
		};
		this.readAsset = async (opts) => {
			try {
				const assetId = this.store.getState().asset?.id;
				if (!assetId) throw new Error("Asset does not have ID to query!");
				const asset = await params.readAsset(assetId, opts);
				this.store.setState((s) => {
					s.asset = asset;
				});
				return asset;
			} catch (err) {
				this.store.setState((s) => {
					s.errors = [...s.errors, err];
				});
			}
		};
		if (params.pollAssetCfg) this.setState((s) => {
			s.pollAssetState = "idle";
		});
		this.debug = params.debug ?? false;
		uploadManager.add(this);
		this._log("Constructed Upload");
	}
	async pollAsset() {
		if (!this.poller) {
			this._log("Nothing to poll");
			return;
		}
		this.setState((s) => {
			s.pollAssetState = "active";
		});
		this.poller.start();
		this._log("Polling...");
		const res = await this.poller.result();
		this._log(`Polling finished! (reason: ${res.reason})`);
		this.setState((s) => {
			switch (res.reason) {
				case "aborted":
					s.pollAssetState = "aborted";
					break;
				case "limit-exceeded":
					s.pollAssetState = "failed";
					break;
				case "success":
					s.pollAssetState = "finished";
					break;
			}
		});
	}
	async handleUploadFinished() {
		this._log("Upload finished");
		this.store.setState((s) => {
			s.progress = 1;
			s.state = "finished";
		});
		this.pollAsset();
		if (uploadManager.getSettings().startNextInQueueOn.includes("finished")) uploadManager.startNextQueuedUpload();
	}
	async handleUploadFailed() {
		this.store.setState((s) => {
			s.state = "failed";
		});
		this._log("Upload failed");
		if (uploadManager.getSettings().startNextInQueueOn.includes("failed")) uploadManager.startNextQueuedUpload();
	}
	async start(force = false) {
		const { state } = this.store.getState();
		if (state === "finished" || state === "aborted" || state === "failed") return;
		if (state === "active") return;
		if (!uploadManager.canStartUpload() && !force) {
			this._log("Queuing");
			this.store.setState((s) => {
				s.state = "queued";
			});
			return;
		}
		this.store.setState((s) => {
			s.state = "active";
		});
		if (!this.store.getState().info) {
			this._log("Need to create upload first...");
			await this.createUpload();
		}
		return true;
	}
	pause() {
		if (!this.canPause) return;
		const { state } = this.store.getState();
		if (state === "finished" || state === "aborted" || state === "failed") return;
		if (state !== "active") return;
		this.store.setState((s) => {
			s.state = "paused";
		});
		if (uploadManager.getSettings().startNextInQueueOn.includes("paused")) uploadManager.startNextQueuedUpload();
	}
	abort() {
		const { state } = this.store.getState();
		if (state === "finished" || state === "aborted" || state === "failed") return;
		this.store.setState((s) => {
			s.state = "aborted";
		});
		if (uploadManager.getSettings().startNextInQueueOn.includes("aborted")) uploadManager.startNextQueuedUpload();
		return true;
	}
	remove() {
		const { state } = this.store.getState();
		if (!(state === "finished" || state === "aborted" || state === "failed" || state === "idle")) return;
		if (this.poller && this.poller.getState().state === "active") {
			this.poller.abort();
			this.setState((s) => {
				s.pollAssetState = "aborted";
			});
		}
		uploadManager.remove(this);
	}
};

//#endregion
export { Upload };